import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  Link,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';

import { fetchTeamRanking } from './RankingApi.jsx';
import './TeamRanking.css';

const getTeamPhoto = (team) => team?.logoUrl || '';

const buildTeamUrl = (team) => {
  if (!team?._id) return null;

  const teamLabel = team?.name || team?.fullName || team?._id;
  return `/dsas-view/equipe/${encodeURIComponent(teamLabel)}_${team._id}`;
};

const formatDecimal = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const getColumnsByStatType = (statType, discipline) => {
  const isSoccer = discipline === 'soccer';
  const isRugby = discipline === 'rugby';

  switch (statType) {
    case 'best_attack':
    case 'best_attack_home':
    case 'best_attack_away':
      // return {
      //   mainLabel: isSoccer ? 'buts marqués' : isRugby ? 'points marqués' : 'points',
      //   ratioLabel: isSoccer ? 'buts / match' : isRugby ? 'points / match' : 'points / match',
      // };
     return {
        mainLabel: 'points',
        ratioLabel: 'points / match',
      };

    case 'best_defense':
    case 'best_defense_home':
    case 'best_defense_away':
      return {
        mainLabel: isSoccer ? 'buts encaissés' : isRugby ? 'points encaissés' : 'points encaissés',
        ratioLabel: isSoccer ? 'buts / match' : isRugby ? 'points / match' : 'encaissés / match',
      };

    case 'best_try_scorers':
      return {
        mainLabel: 'essais marqués',
        ratioLabel: 'essais / match',
      };

    case 'best_try_defenders':
      return {
        mainLabel: 'essais encaissés',
        ratioLabel: 'essais / match',
      };

    default:
      return {
        mainLabel: 'valeur',
        ratioLabel: 'valeur / match',
      };
  }
};

const cx = (...classes) => classes.filter(Boolean).join(' ');

export default function TeamRankingSection({
  statType,
  discipline,
  competitionIds = [],
  season,
  widgetConfigurationId = null,
  limit = 20,
  limitOptions = [10, 20, 50, 100],
  sort = 'value_desc',
  onNavigate,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(limit);
  const [data, setData] = useState({
    items: [],
    total: 0,
    totalPages: 0,
    page: 1,
    limit,
  });

  const labels = useMemo(() => getColumnsByStatType(statType, discipline), [statType, discipline]);
  const competitionKey = competitionIds?.join(',') || '';

  useEffect(() => {
    setRowsPerPage(limit);
    setPage(1);
  }, [limit]);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const result = await fetchTeamRanking({
          discipline,
          statType,
          competitionIds,
          season,
          widgetConfigurationId,
          page,
          limit: rowsPerPage,
          sort,
        });

        if (!ignore) {
          setData(result);
        }
      } catch (e) {
        if (!ignore) {
          setError('Impossible de charger le classement.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [page, discipline, statType, competitionKey, season, widgetConfigurationId, rowsPerPage, sort]);

  useEffect(() => {
    setPage(1);
  }, [statType, discipline, season, competitionKey, widgetConfigurationId]);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleLinkClick = (event, url) => {
    if (!url || !onNavigate) return;
    event.preventDefault();
    onNavigate(url);
  };

  const renderTable = () => (
    <TableContainer className="team-ranking-section__table-card">
      <Table size="small" className="team-ranking-section__table" aria-label="Classement des statistiques équipes">
        <colgroup>
            <col className="team-ranking-section__col-rank" />
            <col className="team-ranking-section__col-team" />
            <col className="team-ranking-section__col-ratio" />
            <col className="team-ranking-section__col-matches" />
            <col className="team-ranking-section__col-value" />
          </colgroup>
        <TableHead>
          <TableRow>
            <TableCell className="team-ranking-section__head-cell team-ranking-section__rank-cell">#</TableCell>
            <TableCell className="team-ranking-section__head-cell ">Équipe</TableCell>
            <TableCell className="team-ranking-section__head-cell" align="right">
              {labels.ratioLabel}
            </TableCell>
            <TableCell className="team-ranking-section__head-cell" align="right">
              MJ
            </TableCell>
            <TableCell className="team-ranking-section__head-cell" align="right">
              {labels.mainLabel}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {data.items.map((item, index) => {
            const rank = (data.page - 1) * data.limit + index + 1;
            const teamUrl = buildTeamUrl(item.team);
            const teamName = item.team?.name || item.team?.fullName || '-';

            return (
              <TableRow key={item.team?._id || `${item._id}-${index}`} hover className="team-ranking-section__table-row">
                <TableCell className="team-ranking-section__body-cell team-ranking-section__rank-cell">{rank}</TableCell>

                <TableCell className="team-ranking-section__body-cell">
                  <Box className="team-ranking-section__team-stack">
                    <Avatar src={getTeamPhoto(item.team)} alt={teamName} className="team-ranking-section__team-avatar">
                      <GroupsRoundedIcon fontSize="small" />
                    </Avatar>

                    <Box className="team-ranking-section__team-content">
                      <Typography className="team-ranking-section__team-name" noWrap={isMobile}>
                        {teamUrl ? (
                          <Link href={teamUrl} underline="hover" onClick={(event) => handleLinkClick(event, teamUrl)}>
                            {teamName}
                          </Link>
                        ) : (
                          teamName
                        )}
                      </Typography>

                      {item.team?.fullName && item.team?.fullName !== item.team?.name ? (
                        <Typography component="span" className="team-ranking-section__full-name" noWrap={isMobile}>
                          {item.team.fullName}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </TableCell>

                <TableCell className="team-ranking-section__body-cell" align="right">
                  {formatDecimal(item.valuePerMatch)}
                </TableCell>

                <TableCell className="team-ranking-section__body-cell" align="right">
                  {item.matchesCount ?? '-'}
                </TableCell>

                <TableCell className="team-ranking-section__body-cell" align="right">
                  <Box component="span" className="team-ranking-section__value-pill">
                    {item.value ?? 0}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box className="team-ranking-section">
      {loading ? (
        <Box className="team-ranking-section__feedback">
          <CircularProgress size={26} className="team-ranking-section__loader" />
        </Box>
      ) : error ? (
        <Typography className={cx('team-ranking-section__feedback', 'team-ranking-section__feedback--error')}>
          {error}
        </Typography>
      ) : data.items?.length === 0 ? (
        <Typography className="team-ranking-section__feedback">Aucun résultat.</Typography>
      ) : (
        <>
          {renderTable()}

          <Box className="team-ranking-section__footer">
            {data.totalPages > 1 ? (
              <Pagination
                page={page}
                count={data.totalPages}
                onChange={(_, value) => setPage(value)}
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 1}
                className="team-ranking-section__pagination"
              />
            ) : (
              <Box />
            )}

            <FormControl size="small" className="team-ranking-section__limit-control">
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange} renderValue={(value) => `${value} / page`}>
                {limitOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option} / page
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
    </Box>
  );
}
